<template>
  <div
    class="app-search"
    :class="['app-search--' + variant, { 'is-open': open }]"
    :data-test="isSheet ? 'app-shell-search-panel' : null"
    :role="isSheet ? 'dialog' : null"
    :aria-label="isSheet ? 'Search patients, tasks or pages' : null"
  >
    <div class="app-search__field">
      <span class="app-search__icon"><AppIcon name="search" :size="isSheet ? 20 : 18" /></span>
      <input
        ref="input"
        v-model="q"
        type="search"
        class="app-search__input"
        :class="{ 'app-search__input--sheet': isSheet }"
        placeholder="Search patients, tasks or pages"
        aria-label="Search patients, tasks or pages"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="panelShown ? 'true' : 'false'"
        :aria-controls="listId"
        :aria-activedescendant="activeRowId"
        data-test="app-shell-search-input"
        @focus="onFocus"
        @input="onInput"
        @keydown="onKeydown"
      />
      <span v-if="!isSheet && kbd" class="app-search__kbd" aria-hidden="true">{{ kbd }}</span>
      <button
        v-if="isSheet"
        type="button"
        class="app-search__cancel"
        data-test="app-search-cancel"
        @mousedown.prevent
        @click="cancel"
      >Cancel</button>
    </div>

    <div
      v-if="panelShown"
      class="app-search__panel"
      :data-test="isSheet ? null : 'app-shell-search-panel'"
    >
      <div :id="listId" class="app-search__results" role="listbox" aria-label="Search results">
        <template v-if="state.pages.length">
          <div class="app-search__label" role="presentation">Go to</div>
          <div
            v-for="(page, i) in state.pages"
            :id="rowId(i)"
            :key="'p-' + page.id"
            class="app-search__row app-search__row--page"
            :class="{ 'is-active': activeIndex === i }"
            role="option"
            :aria-selected="activeIndex === i ? 'true' : 'false'"
            :data-test="'app-search-page-' + page.id"
            @mousedown.prevent="pickPage(page)"
            @mousemove="hover(i)"
          >
            <span class="app-search__page-icon"><AppIcon :name="page.icon" :size="16" /></span>
            <span class="app-search__page-text">
              <span class="app-search__page-label">{{ page.label }}</span>
              <span v-if="page.sub" class="app-search__page-sub">{{ page.sub }}</span>
            </span>
          </div>
        </template>

        <template v-if="state.commands.length">
          <div class="app-search__label" role="presentation" data-test="app-search-command-title">Commands</div>
          <div
            v-for="(cmd, c) in state.commands"
            :id="rowId(state.pages.length + c)"
            :key="'c-' + cmd.id"
            class="app-search__row app-search__row--page"
            :class="{ 'is-active': activeIndex === state.pages.length + c }"
            role="option"
            :aria-selected="activeIndex === state.pages.length + c ? 'true' : 'false'"
            :data-test="'app-search-command-' + cmd.id"
            @mousedown.prevent="pickPage(cmd)"
            @mousemove="hover(state.pages.length + c)"
          >
            <span class="app-search__page-icon"><AppIcon :name="cmd.icon" :size="16" /></span>
            <span class="app-search__page-text">
              <span class="app-search__page-label">{{ cmd.label }}</span>
              <span v-if="cmd.sub" class="app-search__page-sub">{{ cmd.sub }}</span>
            </span>
          </div>
        </template>

        <template v-if="state.patients.length">
          <div class="app-search__label" role="presentation" data-test="app-search-patient-title">{{ state.patientTitle }}</div>
          <div
            v-for="(patient, j) in state.patients"
            :id="rowId(patientsAt + j)"
            :key="'pt-' + patient.PatNum"
            class="app-search__row app-search__row--patient"
            :class="{ 'is-active': activeIndex === patientsAt + j }"
            role="option"
            :aria-selected="activeIndex === patientsAt + j ? 'true' : 'false'"
            :data-test="'app-search-patient-' + patient.PatNum"
            @mousedown.prevent="pickPatient(patient)"
            @mousemove="hover(patientsAt + j)"
          >
            <span class="app-search__avatar" aria-hidden="true">{{ initials(patient) }}</span>
            <span class="app-search__name">{{ patientName(patient) }}</span>
            <span v-if="dob(patient)" class="app-search__dob">{{ dob(patient) }}</span>
          </div>
          <div v-if="state.morePatients > 0" class="app-search__more" data-test="app-search-patient-more" role="presentation">+{{ state.morePatients }} more — add a last name to narrow</div>
        </template>

        <template v-if="state.tasks.length">
          <div class="app-search__label" role="presentation" data-test="app-search-task-title">Tasks</div>
          <div
            v-for="(task, k) in state.tasks"
            :id="rowId(tasksAt + k)"
            :key="'t-' + task.id"
            class="app-search__row app-search__row--page"
            :class="{ 'is-active': activeIndex === tasksAt + k }"
            role="option"
            :aria-selected="activeIndex === tasksAt + k ? 'true' : 'false'"
            :data-test="'app-search-task-' + task.id"
            @mousedown.prevent="pickTask(task)"
            @mousemove="hover(tasksAt + k)"
          >
            <span class="app-search__page-icon"><AppIcon name="checkSq" :size="16" /></span>
            <span class="app-search__page-text">
              <span class="app-search__page-label">{{ task.text || 'Untitled task' }}</span>
              <span v-if="task.sub" class="app-search__page-sub">{{ task.sub }}</span>
            </span>
          </div>
        </template>

        <div v-if="state.none" class="app-search__empty" data-test="app-search-empty">
          Nothing matches “{{ q.trim() }}”. New patient today? Refresh the list.
        </div>
        <div v-else-if="!rows.length" class="app-search__empty app-search__empty--hint" data-test="app-search-hint">
          Type a patient’s name, a task or a page.
        </div>
        <div v-if="showTasksHint" class="app-search__empty app-search__empty--hint" data-test="app-search-tasks-hint" :data-status="tasksStatus" role="status">
          {{ tasksStatus === 'failed' ? 'Tasks could not be loaded just now. Close the search and open it again to retry.' : 'Loading tasks…' }}
        </div>
      </div>

      <div class="app-search__footer">
        <span class="app-search__sync" data-test="app-search-sync">{{ syncText }}</span>
        <button
          type="button"
          class="app-search__refresh"
          data-test="app-search-refresh"
          :disabled="refreshing"
          :aria-busy="refreshing ? 'true' : 'false'"
          @mousedown.prevent
          @click="onRefresh"
        >
          <span v-if="refreshing" class="app-search__spin" data-test="app-search-refresh-spinner"><AppIcon name="refresh" :size="14" :stroke="2.4" /></span>
          <AppIcon v-else name="refresh" :size="14" />
          {{ isSheet ? 'Refresh' : 'Refresh patient list' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * App shell — the top-bar search (design 1d; full-screen sheet on phones, 1h).
 *
 * ONE box finds pages, Tasks commands, patients AND tasks (screen order). The rules are
 * src/utils/nav/search.js (`paletteState`, `syncLabel`); patient matching is src/utils/patientSearch.js,
 * the same matcher as the classic HeaderPatientSearch.vue (names in any order; DOB shown only to tell
 * same-named patients apart). A command is a page whose `to` carries a query (buildCommands), so it
 * leaves through the same `pick-page`; a task row leaves through `pick-task`. At most 20 patients are
 * listed; when more match, a non-pickable "+N more — add a last name to narrow" line follows the last
 * one (never in `rows`, so the arrows and Enter skip it).
 *
 * No Vuex, no router: AppShell passes data in and acts on what this emits.
 *   props  variant 'bar'|'fullscreen', open, patients, recentPatNums, pages (buildPages),
 *          commands (buildCommands), tasks ({ id, text, sub, hay? } rows), tasksStatus ('ready'|'loading'|
 *          'failed' — while not ready a typed query shows "Loading tasks…" / the failure line), dateFormat, refreshing, lastSyncedAt (ms|null), kbd
 *   emits  open, close, pick-page(to), pick-patient(patient), pick-task(row), refresh
 *   exposes focus()  — synchronous, so a phone tap raises the keyboard (iOS ignores a focus()
 *          that runs a tick later, outside the tap)
 *
 * Patient names arrive already masked when the demo mask is on (the data boundary,
 * src/plugins/phiMaskInterceptor.js) — never mask here.
 */
import AppIcon from './AppIcon.vue';
import { paletteState, syncLabel } from '@/utils/nav/search';
import { buildPatientSearchIndex, formatDob, searchPatientsWithCount } from '@/utils/patientSearch';

const MAX_PATIENTS = 20;
let uid = 0;

export default {
  name: 'AppSearch',
  components: { AppIcon },

  props: {
    variant: { type: String, default: 'bar', validator: (v) => ['bar', 'fullscreen'].indexOf(v) > -1 },
    open: { type: Boolean, default: false },
    patients: { type: Array, default: () => [] },
    recentPatNums: { type: Array, default: () => [] },
    pages: { type: Array, default: () => [] },
    commands: { type: Array, default: () => [] },
    tasks: { type: Array, default: () => [] },
    tasksStatus: { type: String, default: 'ready', validator: (v) => ['ready', 'loading', 'failed'].indexOf(v) > -1 },
    dateFormat: { type: String, default: 'MM/DD/YYYY' },
    refreshing: { type: Boolean, default: false },
    lastSyncedAt: { type: Number, default: null },
    kbd: { type: String, default: 'Ctrl K' },
  },

  data() {
    uid += 1;
    return {
      q: '',
      highlight: -1,
      now: Date.now(),
      listId: `app-search-list-${uid}`,
    };
  },

  computed: {
    isSheet() {
      return this.variant === 'fullscreen';
    },

    panelShown() {
      return this.isSheet || this.open;
    },

    // Built once per roster change, not per keystroke.
    searchIndex() {
      return buildPatientSearchIndex(this.patients || []);
    },

    patientsByPatNum() {
      const map = new Map();
      (this.patients || []).forEach((p) => {
        if (p && p.PatNum != null) map.set(String(p.PatNum), p);
      });
      return map;
    },

    // A recent PatNum no longer in the roster is skipped (same as HeaderPatientSearch).
    recentPatients() {
      return (this.recentPatNums || []).map((n) => this.patientsByPatNum.get(String(n))).filter(Boolean);
    },

    // { patients, total } — `total` is every match before the cap, for the "+N more" line.
    matchedPatients() {
      return this.q.trim()
        ? searchPatientsWithCount(this.searchIndex, this.q, MAX_PATIENTS)
        : { patients: [], total: 0 };
    },

    state() {
      return paletteState({
        q: this.q,
        pages: this.pages,
        commands: this.commands,
        patients: this.matchedPatients.patients,
        patientTotal: this.matchedPatients.total,
        recent: this.recentPatients,
        tasks: this.tasks,
      });
    },

    // Row index where the patients / the tasks start (pages and commands come first).
    patientsAt() {
      return this.state.pages.length + this.state.commands.length;
    },
    tasksAt() {
      return this.patientsAt + this.state.patients.length;
    },

    // Tasks are matched once they are loaded; until then (or if the read failed) a typed query says so.
    showTasksHint() {
      return this.tasksStatus !== 'ready' &&this.commands.length > 0 && this.q.trim().length > 0;
    },

    // Every pickable row, in screen order: pages, commands, patients, tasks.
    rows() {
      return this.state.pages.map((page) => ({ type: 'page', page }))
        .concat(this.state.commands.map((page) => ({ type: 'page', page })))
        .concat(this.state.patients.map((patient) => ({ type: 'patient', patient })))
        .concat(this.state.tasks.map((task) => ({ type: 'task', task })));
    },

    // The row Enter opens: the arrowed-to row, else `first` (always row 0 when it exists).
    activeIndex() {
      if (this.highlight >= 0 && this.highlight < this.rows.length) return this.highlight;
      return this.state.first ? 0 : -1;
    },

    activeRowId() {
      return this.panelShown && this.activeIndex >= 0 ? this.rowId(this.activeIndex) : null;
    },

    syncText() {
      return syncLabel(this.lastSyncedAt, this.now, this.refreshing);
    },
  },

  watch: {
    q() {
      this.highlight = -1;
    },
    open(isOpen) {
      this.highlight = -1;
      this.now = Date.now();
      if (!isOpen && this.isSheet) this.q = '';
    },
  },

  mounted() {
    this.clock = setInterval(() => { this.now = Date.now(); }, 30000);
  },

  beforeDestroy() {
    clearInterval(this.clock);
  },

  methods: {
    rowId(i) {
      return `${this.listId}-row-${i}`;
    },

    /** Focus the input SYNCHRONOUSLY; the sheet is revealed on the element first (Vue's class patch lands a tick later). */
    focus() {
      if (this.isSheet && this.$el && this.$el.classList) this.$el.classList.add('is-open');
      const input = this.$refs.input;
      if (input && typeof input.focus === 'function') input.focus();
    },

    blur() {
      const input = this.$refs.input;
      if (input && typeof input.blur === 'function') input.blur();
    },

    onFocus() {
      this.now = Date.now();
      if (!this.open) this.$emit('open');
    },

    onInput() {
      if (!this.open) this.$emit('open');
    },

    close() {
      if (this.isSheet && this.$el && this.$el.classList) this.$el.classList.remove('is-open');
      this.$emit('close');
      this.blur();
    },

    cancel() {
      this.q = '';
      this.close();
    },

    hover(i) {
      this.highlight = i;
    },

    onKeydown(e) {
      const n = this.rows.length;
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        this.close();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!n) return;
        this.highlight = Math.min(this.activeIndex + 1, n - 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (!n) return;
        this.highlight = Math.max(this.activeIndex - 1, 0);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const row = this.rows[this.activeIndex];
        if (!row) return;
        if (row.type === 'page') this.pickPage(row.page);
        else if (row.type === 'task') this.pickTask(row.task);
        else this.pickPatient(row.patient);
      }
    },

    pickTask(task) {
      if (!task) return;
      this.$emit('pick-task', task);
      this.q = '';
      this.close();
    },

    pickPage(page) {
      if (!page) return;
      this.$emit('pick-page', page.to);
      this.q = '';
      this.close();
    },

    pickPatient(patient) {
      if (!patient) return;
      this.$emit('pick-patient', patient);
      this.q = '';
      this.close();
    },

    onRefresh() {
      if (this.refreshing) return;
      this.$emit('refresh');
    },

    patientName(p) {
      if (!p) return '';
      const last = (p.LName || '').trim();
      const first = (p.FName || '').trim();
      return last && first ? `${last}, ${first}` : (last || first);
    },

    initials(p) {
      if (!p) return '';
      return `${(p.FName || '').trim().charAt(0)}${(p.LName || '').trim().charAt(0)}`.toUpperCase();
    },

    dob(p) {
      return p ? formatDob(p.Birthdate, this.dateFormat) : '';
    },
  },
};
</script>

<style lang="scss" scoped>
@import './shell';

.app-search {
  position: relative;
  width: 100%;
  font-family: $fu-font-sans;
}

// ── The field ──────────────────────────────────────────────────────────────────────────────
.app-search__field {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 40px;
  padding: 0 10px 0 12px;
  border: 1px solid $fu-border;
  border-radius: 10px;
  background: $fu-neutral-50;
  transition: background $fu-dur-fast $fu-ease-out, border-color $fu-dur-fast $fu-ease-out, box-shadow $fu-dur-fast $fu-ease-out;
}

.app-search__icon {
  display: flex;
  color: $fu-neutral-400;
}

.app-search--bar.is-open .app-search__field,
.app-search--bar .app-search__field:focus-within {
  border-color: $fu-brand-300;
  background: $fu-surface;
  box-shadow: $fu-ring;

  .app-search__icon { color: $fu-brand; }
}

.app-search__input {
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  color: $fu-text;
  -webkit-appearance: none;
  appearance: none;

  &::placeholder { color: $fu-neutral-500; }
  &::-webkit-search-cancel-button { display: none; }
}

.app-search__kbd {
  flex: none;
  padding: 1px 6px;
  border: 1px solid $fu-border;
  border-radius: 5px;
  background: $fu-surface;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  color: $fu-neutral-500;
}

// ── The results popover (bar) ──────────────────────────────────────────────────────────────
.app-search--bar .app-search__panel {
  position: absolute;
  top: 46px;
  left: 0;
  z-index: $shell-z-popover;
  width: $shell-search-popover;
  max-width: calc(100vw - 24px);
  display: flex;
  flex-direction: column;
  background: $fu-surface;
  border: 1px solid $fu-border;
  border-radius: 14px;
  box-shadow: $fu-shadow-lg;
  overflow: hidden;
  animation: app-search-pop 0.14s ease;
}

@keyframes app-search-pop {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: none; }
}

.app-search__results {
  max-height: 340px;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 6px;
}

.app-search__label {
  @include shell-section-label;
  padding: 8px 10px 4px;
}

.app-search__row {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 40px;
  padding: 0 10px;
  border-radius: 8px;
  cursor: pointer;
  color: $fu-text;

  &.is-active { background: $fu-neutral-50; }
}

.app-search__page-icon {
  width: 28px;
  height: 28px;
  flex: none;
  border-radius: 8px;
  border: 1px solid $fu-border;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $fu-neutral-600;
}

.app-search__page-text {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.app-search__page-label {
  font-size: 14px;
  font-weight: $fu-fw-semibold;
  white-space: nowrap;
}

.app-search__page-sub {
  min-width: 0;
  font-size: 12.5px;
  color: $fu-neutral-500;
  @include fu-ellipsis;
}

.app-search__avatar {
  width: 24px;
  height: 24px;
  flex: none;
  border-radius: 50%;
  background: $fu-brand-50;
  color: $fu-brand-700;
  font-size: 10px;
  font-weight: $fu-fw-bold;
  display: flex;
  align-items: center;
  justify-content: center;
}

.app-search__name {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: $fu-fw-semibold;
  @include fu-ellipsis;
}

.app-search__dob {
  flex: none;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  color: $fu-neutral-500;
}

.app-search__empty {
  padding: 14px 10px;
  font-size: 13.5px;
  color: $fu-neutral-500;
}

// "+N more — add a last name to narrow": a hint under the last patient row, not a result row
// (no hover, no pointer, not in `rows`).
.app-search__more {
  padding: 4px 10px 8px;
  font-size: 12.5px;
  color: $fu-neutral-500;
}

.app-search__footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid $fu-border;
  background: $fu-neutral-25;
  font-size: 12.5px;
  color: $fu-neutral-500;
}

.app-search__sync { min-width: 0; @include fu-ellipsis; }

.app-search__refresh {
  @include fu-button-reset;
  @include fu-focus-ring;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: none;
  border-radius: 6px;
  font-weight: $fu-fw-bold;
  color: $fu-brand-700;

  &:disabled { cursor: default; opacity: 0.75; }
}

.app-search__spin {
  display: flex;
  @include fu-spin;
}

// ── Phone: the full-screen sheet (1h) ──────────────────────────────────────────────────────
.app-search--fullscreen {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: $shell-z-popover;
  display: none;
  flex-direction: column;
  background: $fu-surface;

  &.is-open { display: flex; }

  .app-search__field {
    height: 56px;
    flex: none;
    gap: 8px;
    padding: 0 6px 0 14px;
    border: 0;
    border-bottom: 1px solid $fu-border;
    border-radius: 0;
    background: $fu-surface;
  }

  .app-search__icon { color: $fu-brand; }

  .app-search__panel {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .app-search__results {
    flex: 1;
    max-height: none;
  }

  .app-search__label { padding: 10px 10px 4px; }

  .app-search__row {
    height: 52px;
    gap: 12px;
    border-radius: 10px;
  }

  .app-search__page-icon {
    width: auto;
    height: auto;
    border: 0;
  }

  .app-search__page-text {
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
  }

  .app-search__page-label,
  .app-search__name { font-size: 15px; }

  .app-search__avatar {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }

  .app-search__dob { font-size: 12.5px; }

  .app-search__empty { padding: 16px 10px; font-size: 14px; }

  .app-search__more { padding: 6px 10px 10px; font-size: 13px; }

  .app-search__footer {
    padding: 12px 16px 26px;
    font-size: 13px;
  }

  .app-search__refresh { min-height: 36px; }
}

// ⛔ iOS Safari zooms the page when a focused input is under 16px and never zooms back
// (frontend CLAUDE.md, cypress/node-tests/ios-focus-zoom-guardrail.test.js). The phone sheet's
// input is 16px, in px — the app root is 14px, so 1rem would be a zoom trigger.
.app-search--fullscreen .app-search__input,
.app-search__input--sheet {
  height: 44px;
  font-size: 16px;
}

.app-search__cancel {
  @include fu-button-reset;
  @include fu-focus-ring;
  height: 44px;
  flex: none;
  padding: 0 10px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: $fu-fw-semibold;
  color: $fu-brand-700;
}

// ── Tablet bar (1g): 44px field, results capped to the screen ─────────────────────────────
@include shell-tablet {
  .app-search--bar .app-search__field {
    height: 44px;
    border-radius: 12px;
  }

  .app-search--bar .app-search__panel {
    top: 50px;
    width: 440px;
  }
}
</style>
